import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import { isAxiosError } from 'axios';

export interface AddAccountPayload {
  ad_account_id: string;
  ad_account_name: string;
  platform: string;
  app: string;
  ci_upload: boolean;
  is_active: boolean;
  blacklist_check: boolean;
}

interface AddAccountResponse {
  success: boolean;
}

interface ErrorResponse {
  message: string;
  details?: string;
}

export const addAccount = async (
  params: AddAccountPayload,
): Promise<AddAccountResponse> => {
  const res = await apiClient.post<AddAccountResponse>(
    queryKeys.accounts,
    params,
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to add account');
  }
  return res.data;
};

export const updateAccount = async (
  {
    id,
    ...params
  }: AddAccountPayload & { id: number }): Promise<AddAccountResponse> => {
  const res = await apiClient.put<AddAccountResponse>(
    `${queryKeys.accounts}/${id}`,
    params,
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to update account');
  }
  return res.data;
};

type MutationFnType = (
  data: AddAccountPayload,
) => ReturnType<typeof addAccount>;

type UpdateMutationFnType = (
  data: AddAccountPayload & { id: number },
) => ReturnType<typeof updateAccount>;

interface UseOptions {
  config?: MutationConfig<MutationFnType>;
}

interface UseUpdateOptions {
  config?: MutationConfig<UpdateMutationFnType>;
}

export const useAddAccount = ({ config }: UseOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.accounts],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: addAccount,
  });
};

export const useUpdateAccount = ({ config }: UseUpdateOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.accounts],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.accountsDetails],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: updateAccount,
  });
};
